'use client';

import { useEffect, ContextType, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import { VisibilityContext } from 'react-horizontal-scrolling-menu';
import { useLocale, useTranslations } from 'next-intl';
import { useRouter } from '@/navigation';
import { MenuItems, useCurrentProperties } from '@/hooks/query';
import { SportIcon } from '@/components/icons/svg-icons';
import { useSportMenuActived } from '@/hooks/use-sport-menu-actived';
import { ScrollingWrapper } from './scroll-menu';
import { useSiteStore } from '@/store/site';
import { getDummySetting } from '@/utils/saba-sports-util';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';
import { KafkaActionSettings, KafkaMarsTypeEnums, KafkaNote } from '@nf/utils-common/constants';
import { useAppearanceStore } from '@/store/appearance';
import { ViewEnum } from '@/types';

type scrollVisibilityApiType = ContextType<typeof VisibilityContext>;

export const SubMenu = ({ items: subMenuItemsOfCurrentSport }: {items: MenuItems[]}) => {
	const view = useAppearanceStore(state => state.view);
	const { sport, leagueGroupId } = useCurrentProperties();
	const apiRef = useRef({} as scrollVisibilityApiType);
	const { isSportActived, isActivedByLeagueGroupId, isCricketGameTypeActived } = useSportMenuActived();
	const resetAllFilter = useSiteStore(state => state.resetAllFilter);
	const router = useRouter();
	const locale = useLocale();
	const searchParams = useSearchParams();


	const setKafkaPinGoal = (sportId: string) => {
		if (view === ViewEnum.galaxy) return;

		const pinGoalActions = KafkaActionSettings.MarsV1[KafkaMarsTypeEnums.PinGoal];
		const action = {
			'9995': pinGoalActions.PinGoalSoccer,
			'9996': pinGoalActions.PinGoalBasketball,
			'9997': pinGoalActions.PinGoalEsport,
		}[sportId];
		if (action) {
			const kafkaData = {
				note: KafkaNote.MarsV1,
				type: KafkaMarsTypeEnums.PinGoal,
				action
			}
			setKafkaNormal(kafkaData);
		}
	}
	
	const onClick = (item: MenuItems) => () => {
		setKafkaPinGoal(item.gameId);
		router.push(item.href, { locale });
		resetAllFilter();
	}
	

	useEffect(() => {
		const dummySetting = getDummySetting(Number(sport));

		const current = subMenuItemsOfCurrentSport.find(item => dummySetting ? sport === Number(item.gameId) : item.icon === `${sport}_${searchParams.get('lgid')}`) || subMenuItemsOfCurrentSport[0];
		const timer = setTimeout(() => {
			apiRef.current?.scrollToItem?.(
				apiRef.current?.getItemById(String(current?.icon)), 'smooth', 'center', 'nearest'
			);
		}, 100);

		return () => {
			clearTimeout(timer);
		};
	}, [sport, leagueGroupId, subMenuItemsOfCurrentSport]);
    
	return (
		<div className="sports-submenu container" data-open={true}>
			<ScrollingWrapper
				enableLeftArrow={subMenuItemsOfCurrentSport.length > 5}
				apiRef={apiRef}
			>
				{subMenuItemsOfCurrentSport?.map((item, idx) => (
					<div
						key={item.icon}
						className={'sports-submenu__item'}
						data-selected={
							item.gameId === '43' ? isActivedByLeagueGroupId(item.href) : 
								item.gameId === '50' ? isCricketGameTypeActived(item.href, idx) : isSportActived(item.href)
						}
						onClick={onClick(item)}
					>
						{item.icon ? (
							<SportIcon
								sportId={item.icon}
								isColorful={
									(view === ViewEnum.galaxy) || 
									(item.gameId === '43' ? isActivedByLeagueGroupId(item.href) : 
										item.gameId === '50' ? isCricketGameTypeActived(item.href, idx) : isSportActived(item.href))
								}
							/>
						) : null}
						{item.name}
					</div>
				))}
			</ScrollingWrapper>
		</div>
	)
};